import * as THREE from 'three';
import { SURFACE_REST_POSITION_ATTRIBUTE } from './SurfaceCoordinates';
import {
  assignDamageFieldUniforms,
  DAMAGE_FIELD_GLSL,
  damageFieldProgramParts,
  registerDamageFieldConsumer,
} from './DamageFieldBinding';
import {
  applyDamageDecalAtlasDefine,
  assignDamageDecalUniforms,
  DAMAGE_DECAL_ATLAS_DEFINE,
  DAMAGE_DECAL_VERTEX_GLSL,
  DAMAGE_DECAL_VERTEX_SAMPLE,
  damageDecalUniforms,
} from './DamageDecalAtlas';
import {
  bakedDamageAoSnippet,
  bakedDamageColorSnippet,
  bakedDamageFragmentPrelude,
  bakedDamageRoughnessSnippet,
  type BakedDamageHookOpts,
} from './BakedDamageComposite';

export type { BakedDamageHookOpts };
import {
  applyBakeShaderHooks,
} from './SurfaceBakeChunks';
import {
  applyBakedMapSampling,
  BAKE_FRONT_SIZE,
  BAKE_SIDE_SIZE,
  bakeSurfaceMaps,
  createBakedPreviewFaceMeshes,
  disposeBakedPreviewMaterials,
  type BakeWallSpec,
  type BakedSurfaceMaps,
} from './SurfaceBaker';
import type { SurfaceDefinition, SurfaceFace } from './SurfaceDefinition';

/** Shared so bake-down can flip the live composite without a program rebuild. */
export const damageBakeDownUniforms = {
  uBakeDownDecals: damageDecalUniforms.uBakeDownDecals,
};

export { BAKE_FRONT_SIZE, BAKE_SIDE_SIZE };

export interface BakedSurfaceMaterialOpts {
  wall: BakeWallSpec;
  frontSize?: number;
  sideSize?: number;
  /** Toksvig roughness-mip lift. Off until the crawl gate asks for it. */
  toksvig?: boolean;
}

export interface BakedSurfaceMaterialHandle {
  maps: BakedSurfaceMaps;
  materials: THREE.MeshPhysicalMaterial[];
  meshes: THREE.Mesh[];
  bakeMs: number;
  toksvig: boolean;
  rebake(settings: unknown): void;
  dispose(): void;
}

function coatFromSettings(settings: unknown): { clearcoat: number; clearcoatRoughness: number } {
  const s = settings as { clearcoat?: number; clearcoatRoughness?: number } | null;
  return {
    clearcoat: s?.clearcoat ?? 0,
    clearcoatRoughness: s?.clearcoatRoughness ?? 1,
  };
}

function assignMaps(materials: THREE.MeshPhysicalMaterial[], maps: BakedSurfaceMaps): void {
  const byGroup: Record<number, BakedSurfaceMaps['faces']['front']> = {
    0: maps.faces.right,
    1: maps.faces.right,
    2: maps.faces.top,
    3: maps.faces.top,
    4: maps.faces.front,
    5: maps.faces.front,
  };
  for (let i = 0; i < materials.length; i += 1) {
    const face = byGroup[i] ?? maps.faces.front;
    const mat = materials[i];
    mat.map = face.albedo;
    mat.roughnessMap = face.roughness;
    mat.normalMap = face.normal;
    mat.needsUpdate = true;
  }
}

const BAKED_DAMAGE_OPTS = new WeakMap<THREE.Material, BakedDamageHookOpts>();

export function getBakedDamageOpts(material: THREE.Material): BakedDamageHookOpts | undefined {
  return BAKED_DAMAGE_OPTS.get(material);
}

export function isBakedDamageMaterial(material: THREE.Material): boolean {
  return BAKED_DAMAGE_OPTS.has(material);
}

/**
 * C2 hook: when a damage field is bound, composite blotch/crack/core onto the
 * baked albedo + roughness. Live maps stay the authoring source; demolition
 * binds the volume without swapping this material architecture.
 */
export function attachBakedDamageHook(
  material: THREE.MeshPhysicalMaterial,
  opts: BakedDamageHookOpts = {},
): void {
  BAKED_DAMAGE_OPTS.set(material, opts);
  material.userData.bakedDamage = opts;
  registerDamageFieldConsumer(material);
  const exterior = opts.face !== 'interior';
  const decalEnabled =
    exterior &&
    (opts.profileId === 'concrete'
      || opts.profileId === 'brick'
      || opts.profileId === 'stone'
      || opts.profileId === 'wood');
  if (decalEnabled) applyDamageDecalAtlasDefine(material);

  const prev = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    assignDamageFieldUniforms(shader);
    if (decalEnabled) assignDamageDecalUniforms(shader);
    Object.assign(shader.uniforms, damageBakeDownUniforms);
    applyBakeShaderHooks(shader, material);
    if (decalEnabled && shader.fragmentShader.includes('if (uBakeOutput < 1.5)')) {
      shader.fragmentShader = shader.fragmentShader.replace(
        `if (uBakeOutput < 1.5) {
    bakeRgb = diffuseColor.rgb;
  } else if (uBakeOutput < 2.5) {`,
        `if (uBakeOutput < 1.5) {
    bakeRgb = diffuseColor.rgb;
  } else if (uBakeOutput < 2.5) {`,
      );
    }
    shader.uniforms.uBakedTextureScale = shader.uniforms.uBakedTextureScale ?? {
      value: opts.textureScale ?? 0.55,
    };
    shader.uniforms.uBakedSeed = shader.uniforms.uBakedSeed ?? { value: opts.seed ?? 17 };
    shader.uniforms.uBakedBrickScale = shader.uniforms.uBakedBrickScale ?? {
      value: opts.brickTextureScale ?? 0.46,
    };
    shader.uniforms.uBakedBrickSeed = shader.uniforms.uBakedBrickSeed ?? {
      value: opts.brickSeed ?? 3187,
    };
    shader.uniforms.uBakedBrickMortarWidth = shader.uniforms.uBakedBrickMortarWidth ?? {
      value: opts.brickMortarWidth ?? 0.05,
    };
    shader.uniforms.uBakedBrickCourseJitter = shader.uniforms.uBakedBrickCourseJitter ?? {
      value: opts.brickCourseJitter ?? 0,
    };
    shader.uniforms.uBakedStoneIrregularity = shader.uniforms.uBakedStoneIrregularity ?? {
      value: opts.stoneBlockIrregularity ?? 0.55,
    };
    if (!shader.vertexShader.includes('vBakedRestPosition')) {
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
attribute vec3 ${SURFACE_REST_POSITION_ATTRIBUTE};
varying vec3 vBakedRestPosition;
varying vec3 vBakedRestNormal;
${decalEnabled ? DAMAGE_DECAL_VERTEX_GLSL : ''}
`,
        )
        .replace(
          '#include <worldpos_vertex>',
          `#include <worldpos_vertex>
vBakedRestPosition = ${SURFACE_REST_POSITION_ATTRIBUTE};
vBakedRestNormal = normalize(normal);
${decalEnabled ? DAMAGE_DECAL_VERTEX_SAMPLE : ''}
`,
        );
    }
    if (!shader.fragmentShader.includes('vBakedRestPosition')) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
varying vec3 vBakedRestPosition;
varying vec3 vBakedRestNormal;
${DAMAGE_FIELD_GLSL}
uniform float uBakedTextureScale;
uniform float uBakedSeed;
${bakedDamageFragmentPrelude(opts)}
`,
      );
    }
    if (!shader.fragmentShader.includes('fillDamageGlobals(vBakedRestPosition')) {
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <color_fragment>', bakedDamageColorSnippet(opts))
        .replace('#include <roughnessmap_fragment>', bakedDamageRoughnessSnippet(opts))
        .replace('#include <aomap_fragment>', bakedDamageAoSnippet(opts));
    }
    prev?.(shader, renderer);
  };
  const prevKey = material.customProgramCacheKey.bind(material);
  material.customProgramCacheKey = () =>
    `baked-surface-${opts.profileId ?? 'generic'}-${opts.face ?? 'exterior'}-formed-grain5-${prevKey()}-${damageFieldProgramParts().join('-')}-${decalEnabled ? DAMAGE_DECAL_ATLAS_DEFINE : 'no-decal'}`;
}

export function cloneBakedDamageMaterial(material: THREE.Material): THREE.Material {
  const opts = BAKED_DAMAGE_OPTS.get(material);
  const clone = material.clone();
  if (opts && clone instanceof THREE.MeshPhysicalMaterial) {
    clone.defines = { ...(material as THREE.MeshPhysicalMaterial).defines };
    attachBakedDamageHook(clone, opts);
  }
  return clone;
}

function canvasPixels(tex: THREE.Texture): { data: Uint8ClampedArray; width: number; height: number } | null {
  const image = tex.image as HTMLCanvasElement | ImageData | undefined;
  if (!image) return null;
  if (image instanceof HTMLCanvasElement) {
    const ctx = image.getContext('2d');
    if (!ctx) return null;
    const img = ctx.getImageData(0, 0, image.width, image.height);
    return { data: img.data, width: image.width, height: image.height };
  }
  if (typeof ImageData !== 'undefined' && image instanceof ImageData) {
    return { data: image.data, width: image.width, height: image.height };
  }
  return null;
}

function writeCanvas(tex: THREE.Texture, data: Uint8ClampedArray, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('BakedSurfaceMaterial: 2d context unavailable');
  const img = ctx.createImageData(width, height);
  img.data.set(data);
  ctx.putImageData(img, 0, 0);
  tex.image = canvas;
  tex.needsUpdate = true;
  return canvas;
}

/**
 * Per-mip Toksvig roughness lift: where the normal mip averages away variance,
 * raise roughness so specular doesn't alias. Level 0 is unchanged.
 */
export function applyToksvigRoughnessMips(maps: BakedSurfaceMaps): void {
  for (const face of Object.values(maps.faces)) {
    const nPix = canvasPixels(face.normal);
    const rPix = canvasPixels(face.roughness);
    if (!nPix || !rPix) continue;
    const mips: HTMLCanvasElement[] = [];
    let nData = nPix.data;
    let rData = rPix.data;
    let w = nPix.width;
    let h = nPix.height;
    while (w > 1 || h > 1) {
      const nw = Math.max(1, w >> 1);
      const nh = Math.max(1, h >> 1);
      const nextN = new Uint8ClampedArray(nw * nh * 4);
      const nextR = new Uint8ClampedArray(nw * nh * 4);
      for (let y = 0; y < nh; y += 1) {
        for (let x = 0; x < nw; x += 1) {
          let nx = 0;
          let ny = 0;
          let nz = 0;
          let rough = 0;
          let count = 0;
          for (let oy = 0; oy < 2; oy += 1) {
            for (let ox = 0; ox < 2; ox += 1) {
              const sx = Math.min(w - 1, x * 2 + ox);
              const sy = Math.min(h - 1, y * 2 + oy);
              const i = (sy * w + sx) * 4;
              nx += nData[i] / 255 * 2 - 1;
              ny += nData[i + 1] / 255 * 2 - 1;
              nz += nData[i + 2] / 255 * 2 - 1;
              rough += rData[i + 1] / 255;
              count += 1;
            }
          }
          nx /= count;
          ny /= count;
          nz /= count;
          rough /= count;
          const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1e-6;
          const inv = 1 / len;
          const toksvig = (1 - len) / (len + 1e-4);
          const lifted = Math.min(1, Math.sqrt(rough * rough + toksvig * 0.45));
          const o = (y * nw + x) * 4;
          nextN[o] = Math.round((nx * inv * 0.5 + 0.5) * 255);
          nextN[o + 1] = Math.round((ny * inv * 0.5 + 0.5) * 255);
          nextN[o + 2] = Math.round((nz * inv * 0.5 + 0.5) * 255);
          nextN[o + 3] = 255;
          const rb = Math.round(lifted * 255);
          nextR[o] = rb;
          nextR[o + 1] = rb;
          nextR[o + 2] = rb;
          nextR[o + 3] = 255;
        }
      }
      const mipCanvas = document.createElement('canvas');
      mipCanvas.width = nw;
      mipCanvas.height = nh;
      const ctx = mipCanvas.getContext('2d');
      if (!ctx) break;
      const mipImg = ctx.createImageData(nw, nh);
      mipImg.data.set(nextR);
      ctx.putImageData(mipImg, 0, 0);
      mips.push(mipCanvas);
      nData = nextN;
      rData = nextR;
      w = nw;
      h = nh;
    }
    face.roughness.generateMipmaps = false;
    face.roughness.minFilter = THREE.LinearMipmapLinearFilter;
    face.roughness.mipmaps = mips;
    face.roughness.needsUpdate = true;
    writeCanvas(face.roughness, rPix.data, rPix.width, rPix.height);
  }
}

function bakeNow(
  renderer: THREE.WebGLRenderer,
  definition: SurfaceDefinition,
  settings: unknown,
  face: SurfaceFace,
  opts: BakedSurfaceMaterialOpts,
): { maps: BakedSurfaceMaps; bakeMs: number; toksvig: boolean } {
  const t0 = performance.now();
  const maps = bakeSurfaceMaps(
    renderer,
    definition,
    settings,
    face,
    { front: opts.frontSize ?? BAKE_FRONT_SIZE, side: opts.sideSize ?? BAKE_SIDE_SIZE },
    {
      width: opts.wall.width,
      height: opts.wall.height,
      thickness: opts.wall.thickness,
      position: opts.wall.position,
    },
    { generateMipmaps: true, anisotropy: 8, keepRenderTargets: true },
  );
  const toksvig = opts.toksvig === true;
  if (toksvig) applyToksvigRoughnessMips(maps);
  return { maps, bakeMs: performance.now() - t0, toksvig };
}

/**
 * Bake albedo / roughness / tangent-normal from the procedural authoring
 * shader and return a live MeshPhysicalMaterial set driven by those maps.
 */
export function createBakedSurfaceMaterial(
  renderer: THREE.WebGLRenderer,
  definition: SurfaceDefinition,
  settings: unknown,
  face: SurfaceFace,
  opts: BakedSurfaceMaterialOpts,
): BakedSurfaceMaterialHandle {
  const first = bakeNow(renderer, definition, settings, face, opts);
  const coat = coatFromSettings(settings);
  const { meshes, materials } = createBakedPreviewFaceMeshes(first.maps, opts.wall, coat);
  for (const mat of materials) {
    mat.dithering = false;
    mat.flatShading = false;
    const settingsRec = settings as {
      textureScale?: number;
      seed?: number;
      mortarWidth?: number;
      courseJitter?: number;
      blockIrregularity?: number;
    } | null;
    attachBakedDamageHook(mat, {
      profileId: definition.id,
      face,
      textureScale: settingsRec?.textureScale,
      seed: settingsRec?.seed,
      brickTextureScale: settingsRec?.textureScale,
      brickSeed: settingsRec?.seed,
      brickMortarWidth: settingsRec?.mortarWidth,
      brickCourseJitter: settingsRec?.courseJitter,
      stoneBlockIrregularity: settingsRec?.blockIrregularity,
    });
  }

  const handle: BakedSurfaceMaterialHandle = {
    maps: first.maps,
    materials,
    meshes,
    bakeMs: first.bakeMs,
    toksvig: first.toksvig,
    rebake(nextSettings: unknown) {
      const prev = handle.maps;
      const next = bakeNow(renderer, definition, nextSettings, face, opts);
      assignMaps(materials, next.maps);
      const coatNext = coatFromSettings(nextSettings);
      for (const mat of materials) {
        mat.clearcoat = coatNext.clearcoat;
        mat.clearcoatRoughness = coatNext.clearcoatRoughness;
      }
      prev.dispose();
      handle.maps = next.maps;
      handle.bakeMs = next.bakeMs;
      handle.toksvig = next.toksvig;
    },
    dispose() {
      for (const mesh of meshes) {
        mesh.removeFromParent();
        mesh.geometry.dispose();
      }
      disposeBakedPreviewMaterials(materials);
      handle.maps.dispose();
    },
  };
  return handle;
}

/** Re-apply sampling if a caller swapped in a raw baker texture. */
export function configureBakedTextures(maps: BakedSurfaceMaps, renderer: THREE.WebGLRenderer): void {
  for (const tex of maps.textures) applyBakedMapSampling(tex, renderer, 8);
}
